/**
 * HTML Sanitization Utility
 *
 * Provides client-side HTML and URL sanitization to prevent XSS attacks.
 * This is defense-in-depth even if server-side sanitization exists.
 */

/**
 * Sanitize HTML content by allowing only safe tags and attributes
 * Strips dangerous elements like <script>, event handlers, style tags, etc.
 *
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Create a temporary DOM element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Allowed tags for rich text content
  const allowedTags = new Set([
    'p', 'br', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre',
    'span', 'div', 'b', 'i'
  ]);

  // Allowed attributes (tag-specific)
  const allowedAttributes = {
    'a': ['href', 'title', 'target', 'rel'],
    'span': ['class'],
    'div': ['class'],
    'code': ['class']
  };

  // Recursively sanitize nodes
  function sanitizeNode(node) {
    // Remove dangerous nodes
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();

      // Remove disallowed tags
      if (!allowedTags.has(tagName)) {
        node.remove();
        return;
      }

      // Sanitize attributes
      const attrs = Array.from(node.attributes);
      for (const attr of attrs) {
        const attrName = attr.name.toLowerCase();

        // Remove all event handlers (onclick, onload, etc.)
        if (attrName.startsWith('on')) {
          node.removeAttribute(attr.name);
          continue;
        }

        // Remove dangerous attributes
        if (['style', 'onerror', 'onload'].includes(attrName)) {
          node.removeAttribute(attr.name);
          continue;
        }

        // Check if attribute is allowed for this tag
        const allowed = allowedAttributes[tagName] || [];
        if (!allowed.includes(attrName)) {
          node.removeAttribute(attr.name);
          continue;
        }

        // Sanitize href/src attributes
        if (attrName === 'href' || attrName === 'src') {
          const sanitizedUrl = sanitizeUrl(attr.value);
          if (sanitizedUrl === '#') {
            // URL was unsafe, remove the attribute
            node.removeAttribute(attr.name);
          } else {
            node.setAttribute(attr.name, sanitizedUrl);
          }
        }
      }

      // Ensure external links have proper rel attribute (noopener only, not noreferrer)
      if (tagName === 'a' && node.hasAttribute('target')) {
        // Always set rel="noopener" - removes noreferrer if present
        node.setAttribute('rel', 'noopener');
      }
    }

    // Recursively sanitize children
    Array.from(node.childNodes).forEach(child => sanitizeNode(child));
  }

  sanitizeNode(temp);
  return temp.innerHTML;
}

/**
 * Sanitize and normalize URLs to prevent javascript: and data: URIs
 *
 * @param {string} url - The URL to sanitize
 * @returns {string} - Sanitized URL or '#' if unsafe
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') {
    return '#';
  }

  // Trim whitespace
  url = url.trim();

  // Empty URL
  if (url.length === 0) {
    return '#';
  }

  // Normalize the URL to detect scheme
  const normalized = url.toLowerCase();

  // Block dangerous protocols
  const dangerousProtocols = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'about:'
  ];

  for (const protocol of dangerousProtocols) {
    if (normalized.startsWith(protocol)) {
      console.warn(`[Security] Blocked unsafe URL with protocol: ${protocol}`);
      return '#';
    }
  }

  // Allow http, https, and relative URLs
  if (
    normalized.startsWith('http://') ||
    normalized.startsWith('https://') ||
    normalized.startsWith('//') ||
    normalized.startsWith('/') ||
    normalized.startsWith('#') ||
    normalized.startsWith('mailto:') // Allow mailto links
  ) {
    // Additional encoding to prevent injection in certain contexts
    try {
      // Parse and reconstruct to normalize
      if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
        const urlObj = new URL(url);
        return urlObj.href;
      }
      return url;
    } catch (e) {
      console.warn(`[Security] Invalid URL format: ${url}`);
      return '#';
    }
  }

  // For other cases, assume it's a relative URL but validate it doesn't contain dangerous characters
  if (/[<>'"]/g.test(url)) {
    console.warn(`[Security] URL contains dangerous characters: ${url}`);
    return '#';
  }

  return url;
}

/**
 * Normalize a human-entered web URL by ensuring an https scheme.
 *
 * - If the value already starts with http:// or https://, return as-is
 * - If it starts with //, prefix with https:
 * - If it starts with '/', '#', 'mailto:', or 'tel:', return as-is
 * - Otherwise, assume a bare domain like "example.com" and prefix with https://
 *
 * @param {string} url - The URL to normalize
 * @returns {string} - A normalized URL string
 */
export function normalizeWebUrl(url) {
  if (!url || typeof url !== 'string') return '';
  let value = url.trim();
  if (value.length === 0) return '';

  const lower = value.toLowerCase();

  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    return value;
  }

  if (value.startsWith('//')) {
    return `https:${value}`;
  }

  if (
    value.startsWith('/') ||
    value.startsWith('#') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('tel:')
  ) {
    return value;
  }

  return `https://${value}`;
}

/**
 * Sanitize text content (strip all HTML)
 *
 * @param {string} text - The text to sanitize
 * @returns {string} - Plain text with HTML removed
 */
export function sanitizeText(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const temp = document.createElement('div');
  temp.textContent = text;
  return temp.textContent;
}
